/**
 * Tokens de tipografía del Design System — FUENTE DE VERDAD.
 *
 * Traslado del frame "Typography" de Figma (node 2:719). Familia única
 * (Plus Jakarta Sans), 4 pesos y una escala de estilos de texto compuestos
 * (Heading / Body / Label / Helper) con su tamaño, interlineado, tracking y peso.
 *
 * Uso en la app (import-only, igual que color — ver ADR-675):
 *   import { TYPE_STYLES, typeStyleCss } from 'src/design-system/tokens/typography';
 *   <p style={typeStyleCss(TYPE_STYLES[0])}>…</p>
 */

export const FONT_FAMILY = 'Plus Jakarta Sans';

/** Muestra de caracteres del specimen. */
export const FONT_CHARSET =
  'ABCDEFGHIJKLMNOPQRSTUVWXYZ\nabcdefghijklmnopqrstuvwxyz\n0123456789 !@#$%^&*()';

export interface FontWeightToken {
  label: string;
  weight: number;
}

export const FONT_WEIGHTS: FontWeightToken[] = [
  { label: 'Regular', weight: 400 },
  { label: 'Medium', weight: 500 },
  { label: 'SemiBold', weight: 600 },
  { label: 'Bold', weight: 700 },
];

/** Categorías de estilo tipográfico (agrupan los estilos en tabs). */
export type TypeCategory = 'Heading' | 'Body' | 'Label' | 'Helper';

/** Orden de las categorías en la UI (tabs). */
export const TYPE_CATEGORIES: TypeCategory[] = ['Heading', 'Body', 'Label', 'Helper'];

export interface TypeStyle {
  /** Categoría del estilo, ej. 'Heading'. */
  category: TypeCategory;
  /** Nombre del estilo, ej. 'Heading / Large strong'. */
  name: string;
  fontSize: number;
  lineHeight: number;
  letterSpacing: number;
  fontWeight: number;
  /** Etiqueta de peso tal como se muestra en el frame, ej. 'SemiBold 600'. */
  weightLabel: string;
  /** Propósito / cuándo usar el estilo. */
  purpose: string;
}

export const TYPE_STYLES: TypeStyle[] = [
  // ── Heading ──
  {
    category: 'Heading',
    name: 'Heading / Extra Large ultra',
    fontSize: 24,
    lineHeight: 31,
    letterSpacing: 0,
    fontWeight: 700,
    weightLabel: 'Bold 700',
    purpose: 'A definir',
  },
  {
    category: 'Heading',
    name: 'Heading / Large strong',
    fontSize: 20,
    lineHeight: 25,
    letterSpacing: 0,
    fontWeight: 600,
    weightLabel: 'SemiBold 600',
    purpose: 'A definir',
  },
  {
    category: 'Heading',
    name: 'Heading / Base strong',
    fontSize: 18,
    lineHeight: 23,
    letterSpacing: 0,
    fontWeight: 600,
    weightLabel: 'SemiBold 600',
    purpose: 'A definir',
  },
  {
    category: 'Heading',
    name: 'Heading / Small strong',
    fontSize: 16,
    lineHeight: 21,
    letterSpacing: 0,
    fontWeight: 600,
    weightLabel: 'SemiBold 600',
    purpose: 'A definir',
  },
  // ── Body ──
  {
    category: 'Body',
    name: 'Body / Base Ultra',
    fontSize: 16,
    lineHeight: 21,
    letterSpacing: 0,
    fontWeight: 700,
    weightLabel: 'Bold 700',
    purpose: '-',
  },
  {
    category: 'Body',
    name: 'Body / Base standard',
    fontSize: 16,
    lineHeight: 21,
    letterSpacing: 0,
    fontWeight: 500,
    weightLabel: 'Medium 500',
    purpose: '-',
  },
  {
    category: 'Body',
    name: 'Body / Small strong',
    fontSize: 14,
    lineHeight: 19,
    letterSpacing: 0,
    fontWeight: 600,
    weightLabel: 'SemiBold 600',
    purpose: '-',
  },
  {
    category: 'Body',
    name: 'Body / Small weak',
    fontSize: 14,
    lineHeight: 19,
    letterSpacing: 0,
    fontWeight: 400,
    weightLabel: 'Regular 400',
    purpose: '-',
  },
  // ── Label ──
  {
    category: 'Label',
    name: 'Label / Base standard',
    fontSize: 16,
    lineHeight: 21,
    letterSpacing: 0,
    fontWeight: 700,
    weightLabel: 'Bold 700',
    purpose: 'Buttons desktop',
  },
  {
    category: 'Label',
    name: 'Label / Small standard',
    fontSize: 14,
    lineHeight: 19,
    letterSpacing: 0,
    fontWeight: 700,
    weightLabel: 'Bold 700',
    purpose: 'Buttons mobile',
  },
  // ── Helper ──
  {
    category: 'Helper',
    name: 'Helper / Base ultra',
    fontSize: 12,
    lineHeight: 17,
    letterSpacing: 0,
    fontWeight: 700,
    weightLabel: 'Bold 700',
    purpose: 'Hints y texto auxiliar',
  },
  {
    category: 'Helper',
    name: 'Helper / Base standard',
    fontSize: 12,
    lineHeight: 17,
    letterSpacing: 0,
    fontWeight: 500,
    weightLabel: 'Medium 500',
    purpose: 'Hints y texto auxiliar',
  },
];

/** Estilos de una categoría puntual, preservando el orden de `TYPE_STYLES`. */
export function typeStylesByCategory(category: TypeCategory): TypeStyle[] {
  return TYPE_STYLES.filter((style) => style.category === category);
}

/** Estilo CSS inline (React-compatible) de un token tipográfico. */
export function typeStyleCss(style: TypeStyle) {
  return {
    fontFamily: `'${FONT_FAMILY}', sans-serif`,
    fontSize: `${style.fontSize}px`,
    lineHeight: `${style.lineHeight}px`,
    fontWeight: style.fontWeight,
    letterSpacing: `${style.letterSpacing}px`,
  };
}
